import PropTypes from 'prop-types';
import { useState } from 'react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { isCombinerNodeName, isSplitterNodeName } from '@/consts';
import usePathwayStore, { pathwayActions } from '@/stores/pathwayStore';
import { nodeActions } from '@/stores/nodeStore';
import { buildHandle } from '@/utils/pathwayUtils';

const getConnectorConfig = node => {
  if (isSplitterNodeName(node?.data?.name)) {
    return { key: 'outputs', handleType: 'output', label: 'Output connectors' };
  }

  if (isCombinerNodeName(node?.data?.name)) {
    return { key: 'inputs', handleType: 'input', label: 'Input connectors' };
  }
};

const connectorButtonClasses =
  'btn btn-xs btn-square rounded border border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 disabled:border-transparent disabled:bg-gray-100 disabled:text-gray-300';

const DynamicConnectorControls = ({ nodeId, locked }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { pathwayId, caseId, node, edgesBySource, edgesByTarget } = usePathwayStore(store => ({
    pathwayId: store.id,
    caseId: store.case_id,
    node: store.nodes.find(node => node.id === nodeId),
    edgesBySource: store.edgesBySource,
    edgesByTarget: store.edgesByTarget,
  }));
  const config = getConnectorConfig(node);

  if (!config) return null;

  const ios = node.data?.[config.key] ?? [];
  const lastIo = ios.at(-1);
  const lastHandle = lastIo ? buildHandle(node.id, config.handleType, lastIo.name) : null;
  const isLastConnected = !!(
    lastHandle && (config.handleType === 'output' ? edgesBySource?.[lastHandle] : edgesByTarget?.[lastHandle])
  );
  const removeDisabled = locked || ios.length <= 2 || isLastConnected;
  const removeTitle =
    ios.length <= 2
      ? 'At least two connectors are required'
      : isLastConnected
        ? 'Disconnect the last connector before removing it'
        : 'Remove connector';
  const controlsDisabled = locked || isUpdating;

  const updateConnector = async action => {
    setIsUpdating(true);

    try {
      await action(node.id);
      await nodeActions.init(pathwayId, caseId, node.id);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="mt-6 mb-2 flex items-center justify-between gap-3 text-sm">
      <div className="flex min-w-0 items-center gap-2">
        <span className="font-semibold text-gray-800">{config.label}</span>
        <span className="min-w-5 rounded bg-gray-100 px-1.5 py-0.5 text-center text-xs font-semibold text-gray-600">
          {ios.length}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          className={connectorButtonClasses}
          onClick={() => updateConnector(pathwayActions.removeNodeIo)}
          disabled={removeDisabled || isUpdating}
          title={removeTitle}
          aria-label="Remove connector"
        >
          <MinusIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          className={connectorButtonClasses}
          onClick={() => updateConnector(pathwayActions.addNodeIo)}
          disabled={controlsDisabled}
          title="Add connector"
          aria-label="Add connector"
        >
          <PlusIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

DynamicConnectorControls.propTypes = {
  nodeId: PropTypes.string.isRequired,
  locked: PropTypes.bool.isRequired,
};

export default DynamicConnectorControls;
